import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  AlertTriangle, Boxes, Truck, Wrench, MessageSquare, Handshake,
  IdCard, FileText, Building2, Users, TrendingUp, CheckCircle2, DollarSign,
  ShoppingBag, ShoppingCart, Briefcase, PackagePlus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const PRIORITY_COLORS = {
  critical: "#991B1B",
  high: "#9A3412",
  medium: "#92400E",
  low: "#166534",
};

function StatCard({ icon: Icon, label, value, sub, accent = "emerald", testid }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    red: "bg-red-50 text-red-900 border-red-200",
    slate: "bg-slate-50 text-slate-900 border-slate-200",
  }[accent];
  return (
    <div
      className="bg-white border border-gray-200 rounded-md p-5 hover:shadow-sm transition-shadow"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center border ${palette}`}>
          <Icon size={17} />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
          {sub}
        </span>
      </div>
      <div className="font-display text-3xl font-bold text-gray-900 leading-none">
        {value ?? "—"}
      </div>
      <div className="text-sm text-gray-600 mt-1.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/kpis");
        setKpis(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const priorityData = Object.entries(kpis?.issues?.by_priority || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1),
    value: v,
    color: PRIORITY_COLORS[k] || "#4B5563",
  }));

  const branchData = Object.entries(kpis?.issues?.by_branch || {}).map(([k, v]) => ({
    branch: k,
    issues: v,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-semibold mb-1.5">
            Control Room
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            Executive Dashboard
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Real-time operational view across all branches.
          </p>
        </div>
        <div className="text-xs text-gray-500">
          Live data · {new Date().toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <StatCard
          icon={AlertTriangle} accent="red"
          label="Open Issues" sub="needs action"
          value={loading ? "…" : kpis?.issues?.open}
          testid="kpi-open-issues"
        />
        <StatCard
          icon={TrendingUp} accent="amber"
          label="Critical Issues" sub="urgent"
          value={loading ? "…" : kpis?.issues?.critical}
          testid="kpi-critical-issues"
        />
        <StatCard
          icon={CheckCircle2} accent="emerald"
          label="Issues Resolved" sub="lifetime"
          value={loading ? "…" : kpis?.issues?.resolved}
          testid="kpi-resolved-issues"
        />
        <StatCard
          icon={Wrench} accent="amber"
          label="Maintenance Open" sub="work orders"
          value={loading ? "…" : kpis?.maintenance?.open}
          testid="kpi-maintenance-open"
        />
        <StatCard
          icon={Boxes} accent="slate"
          label="Assets Tracked" sub={`${kpis?.assets?.in_use ?? 0} in use`}
          value={loading ? "…" : kpis?.assets?.total}
          testid="kpi-assets"
        />
        <StatCard
          icon={Truck} accent="slate"
          label="Vehicles" sub="fleet"
          value={loading ? "…" : kpis?.vehicles?.total}
          testid="kpi-vehicles"
        />
        <StatCard
          icon={Handshake} accent="emerald"
          label="Suppliers" sub="active"
          value={loading ? "…" : kpis?.suppliers?.total}
          testid="kpi-suppliers"
        />
        <StatCard
          icon={IdCard} accent="amber"
          label="Strategic Contacts" sub="business cards"
          value={loading ? "…" : kpis?.contacts?.total}
          testid="kpi-contacts"
        />
        <StatCard
          icon={MessageSquare} accent="red"
          label="Open Complaints" sub="customer feedback"
          value={loading ? "…" : kpis?.feedback?.open_complaints}
          testid="kpi-feedback"
        />
        <StatCard
          icon={Building2} accent="emerald"
          label="Branches" sub="all regions"
          value={loading ? "…" : kpis?.branches?.total}
          testid="kpi-branches"
        />
        <StatCard
          icon={Users} accent="slate"
          label="Active Users" sub="staff"
          value={loading ? "…" : kpis?.users?.total}
          testid="kpi-users"
        />
        <StatCard
          icon={FileText} accent="slate"
          label="Documents" sub="repository"
          value={loading ? "…" : kpis?.documents?.total}
          testid="kpi-documents"
        />
        <StatCard
          icon={DollarSign} accent="emerald"
          label="Sales (live orders)" sub="UGX value"
          value={loading ? "…" : `UGX ${Number(kpis?.sales?.total_value || 0).toLocaleString()}`}
          testid="kpi-sales"
        />
        <StatCard
          icon={ShoppingCart} accent="amber"
          label="Pending Orders" sub={`${kpis?.orders?.total ?? 0} lifetime`}
          value={loading ? "…" : kpis?.orders?.pending}
          testid="kpi-orders-pending"
        />
        <StatCard
          icon={ShoppingBag} accent="slate"
          label="Products Listed" sub="storefront"
          value={loading ? "…" : kpis?.products?.total}
          testid="kpi-products"
        />
        <StatCard
          icon={Briefcase} accent="emerald"
          label="Job Applications" sub="new"
          value={loading ? "…" : kpis?.applications?.received}
          testid="kpi-applications"
        />
        <StatCard
          icon={PackagePlus} accent="red"
          label="Stock Requests" sub="open"
          value={loading ? "…" : kpis?.stock_requests?.open}
          testid="kpi-stock-requests"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Issues by Branch
            </h2>
            <span className="text-[10px] uppercase tracking-widest text-gray-500">
              all priorities
            </span>
          </div>
          <div className="h-72">
            {branchData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">
                No issues reported yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={branchData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="branch" tick={{ fontSize: 11 }} stroke="#6B7280" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#6B7280" />
                  <Tooltip
                    contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid #E5E7EB" }}
                  />
                  <Bar dataKey="issues" fill="#064E3B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-5">
          <h2 className="font-display text-lg font-semibold text-gray-900 mb-4">
            Priority Breakdown
          </h2>
          <div className="h-72">
            {priorityData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">
                No data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={priorityData}
                    dataKey="value"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={3}
                  >
                    {priorityData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ fontSize: 11 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
